import type { CreationBundle, CreationOption, CreationSlot } from '@rollcall/contracts'
import type { TFunction } from 'i18next'
import { termKey } from '../terms.js'
import { equipmentNameKey, slotDictKey } from '../slots.js'

/**
 * Человеческие подписи для того, что приходит ключами.
 *
 * Ключей два вида, и путать их нельзя. Идентификатор записи контента
 * (`srd:dwarf`) переводится словарём терминов, и если перевода нет —
 * показывается оригинальное имя записи, а не пустота и не ключ.
 * Ключ движка (`save.strength`, `armor.light`) — наш собственный
 * идентификатор, у него перевод есть всегда: на экран машинный ключ
 * попадать не должен вовсе.
 *
 * Третий вид — подписи плана создания: заголовок слота и вариант,
 * у которого записи нет («Drow», «Black»). Они приходят с сервера
 * английскими строками из источника, а переводятся по ключу слота
 * (`slots.ts`): имя из источника меняется вместе с записью, ключ —
 * нет.
 */

const ABILITY_NAMES = [
  'strength',
  'dexterity',
  'constitution',
  'intelligence',
  'wisdom',
  'charisma',
] as const

type AbilityName = (typeof ABILITY_NAMES)[number]

function isAbility(value: string): value is AbilityName {
  return (ABILITY_NAMES as readonly string[]).includes(value)
}

/**
 * Имя записи контента. Оригинал приходит с сервера вместе с записью
 * и служит запасным вариантом: непереведённая запись показывается
 * по-английски, а не ключом.
 */
export function contentName(t: TFunction, id: string, original?: string): string {
  return t(`content.${termKey(id)}`, { defaultValue: original ?? termKey(id) })
}

/**
 * Название особенности внутри записи: черта вида, особенность класса
 * или подкласса.
 *
 * Ключ — `key` самой особенности (`second_wind`), а не пара «запись
 * плюс ключ»: во всём SRD один и тот же ключ несёт одно и то же
 * название, и это проверяется тестом.
 *
 * У предыстории особенность одна и зовётся как сама запись
 * («Послушник»): её имя переводится словарём терминов, поэтому
 * запасной вариант здесь — не английский оригинал, а перевод записи.
 */
export function featureName(
  t: TFunction,
  key: string,
  original: string,
  source: string | null = null,
): string {
  const translated = t(`feature.${key}`, { defaultValue: '' })
  if (translated !== '') return translated
  // План повышения уровня записи не называет — там запасной вариант
  // только английский оригинал, и это правильно: машинного ключа
  // на экране не появится ни в каком случае.
  return source === null ? original : contentName(t, source, original)
}

/**
 * Название счётчика ресурса (D56): «Божественный канал», а не
 * `Channel Divinity`.
 *
 * Счётчик объявляет запись контента, и его ключ — тот же, что у
 * особенности, при которой он стоит: во всём SRD это одно и то же
 * понятие, и переводится оно одним словарём. Ключ берётся из самого
 * идентификатора счётчика (`srd:cleric#channel_divinity`), а не из
 * поля особенности: счётчик бывает и у записи целиком.
 *
 * Свой счётчик игрока (`own:…`) назван им самим — переводить нечего.
 */
export function resourceName(
  t: TFunction,
  resource: { readonly id: string; readonly name: string; readonly source: string | null },
): string {
  if (resource.source === null) return resource.name
  const key = resource.id.slice(resource.id.lastIndexOf('#') + 1)
  return featureName(t, key, resource.name)
}

/**
 * Признак из набора листа: владения, языки, сопротивления,
 * преимущества и помехи.
 *
 * Набор известен путём величины, а вид признака — приставкой ключа.
 * Разбирать приходится здесь: движок хранит признаки ключами (D21.3),
 * и это правильно — переводить их его работой не является.
 */
/**
 * Вид признака — приставка ключа: спасброски, доспехи, оружие,
 * инструменты, навыки. По ней признаки и разложены на группы: одной
 * строкой «Атлетика, средние доспехи, Интеллект, воровские
 * инструменты» не читается ничего.
 */
export const TRAIT_KINDS = [
  'save',
  'skill',
  'armor',
  'weapon',
  'tool',
  'check',
  'other',
] as const

export type TraitKind = (typeof TRAIT_KINDS)[number]

export function traitKind(set: string, value: string): TraitKind {
  if (set === 'languages' || set === 'resistances') return 'other'
  const kind = value.split('.')[0] ?? ''
  return (TRAIT_KINDS as readonly string[]).includes(kind)
    ? (kind as TraitKind)
    : 'other'
}

export function traitLabel(t: TFunction, set: string, value: string): string {
  if (set === 'languages') return contentName(t, value)
  if (set === 'resistances') {
    return t(`trait.damage.${value}`, { defaultValue: value })
  }

  const [kind, ...rest] = value.split('.')
  const key = rest.join('.')

  if (kind === 'save' && key !== '') {
    return isAbility(key)
      ? t('trait.save', { name: t(`abilities.${key}`) })
      : t(`trait.condition.${key}`, { defaultValue: value })
  }
  if (kind === 'skill' && key !== '') {
    return t('trait.skill', { name: contentName(t, key) })
  }
  if (kind === 'check') {
    // `check.dexterity.stealth`: чем проверяется и что именно.
    const [ability, skill] = rest
    if (ability !== undefined && skill !== undefined && isAbility(ability)) {
      return t('trait.check', {
        ability: t(`abilities.${ability}`),
        skill: contentName(t, skill),
      })
    }
  }
  if (kind === 'armor' || kind === 'weapon' || kind === 'tool') {
    return t(`trait.${kind}.${key}`, { defaultValue: value })
  }

  return t(`trait.damage.${value}`, { defaultValue: value })
}

/**
 * Заголовок слота создания.
 *
 * Ключ слота устойчив, `slot.name` — нет: это строка таблицы
 * источника. Поэтому перевод ищется по ключу, а имя из источника
 * остаётся запасным вариантом — новый слот в контенте появится
 * на экране по-английски, но появится, а не пропадёт.
 */
export function slotTitle(t: TFunction, slot: CreationSlot): string {
  return t(`slot.${slotDictKey(slot.key)}`, {
    // Слоты снаряжения называются вместе с источником: «Стартовое
    // снаряжение: Воин». Имя источника уже переведено — второго
    // перевода класса здесь нет.
    source: contentName(t, slot.source),
    defaultValue: slot.name,
  })
}

/**
 * Подпись варианта слота.
 *
 * Три случая по убыванию точности: вариант своего слота (линия эльфа),
 * запись контента (навык, инструмент), и — если ни того ни другого
 * нет — подпись из источника как есть.
 */
export function optionLabel(
  t: TFunction,
  slot: CreationSlot,
  option: CreationOption,
): string {
  if (option.bundle !== null) return bundleLabel(t, option.bundle)

  const scoped = t(`slotOption.${slotDictKey(slot.key)}.${option.value}`, {
    defaultValue: '',
  })
  return scoped === '' ? contentName(t, option.value, option.label) : scoped
}

/**
 * Стартовый набор словами: «Кольчуга, 2 × Кинжал и 4 зм».
 *
 * Собирается здесь, а не на сервере: сервер присылает состав
 * (`CreationBundle`), потому что перевести готовую английскую фразу
 * было бы нечем.
 */
export function bundleLabel(t: TFunction, bundle: CreationBundle): string {
  const parts = bundle.items.map((item) => {
    const name = equipmentName(t, item.name, item.id)
    return item.quantity > 1
      ? t('wizard.equipment.times', { count: item.quantity, name })
      : name
  })
  if (bundle.goldGp > 0) {
    parts.push(t('wizard.equipment.gold', { count: bundle.goldGp }))
  }

  const last = parts.at(-1) ?? ''
  if (parts.length <= 1) return last
  return t('wizard.equipment.last', { head: parts.slice(0, -1).join(', '), tail: last })
}

/**
 * Название позиции набора.
 *
 * Сначала — сам набор: «Arcane Focus (orb)» это запись плюс уточнение,
 * которого в записи нет. Потом запись по идентификатору: «Daggers»
 * переводится как `srd:dagger`, а множественное число показывает
 * количество рядом. Идентификатора может не быть вовсе — тогда
 * остаётся только первый путь (`slots.ts`, EQUIPMENT_NAMES).
 */
function equipmentName(t: TFunction, name: string, id: string | null): string {
  const exact = t(`equipment.${equipmentNameKey(name)}`, { defaultValue: '' })
  if (exact !== '') return exact
  return id === null ? name : contentName(t, id, name)
}

/**
 * Подписи величин листа, известных наперёд. Пути задаёт движок,
 * а не контент, поэтому список закрыт и живёт здесь.
 */
const VALUE_KEYS: Readonly<Record<string, string>> = {
  ac: 'sheet.ac',
  'hp.max': 'sheet.hpMax',
  initiative: 'sheet.initiative',
  speed: 'sheet.speed',
  proficiency_bonus: 'sheet.proficiencyBonus',
  'senses.darkvision': 'sheet.darkvision',
  'passive.perception': 'sheet.passivePerception',
  'resources.uses': 'sheet.uses',
  'speeds.climb': 'sheet.climb',
  'speeds.fly': 'sheet.fly',
  'speeds.swim': 'sheet.swim',
  proficiencies: 'sheet.proficiencies',
  languages: 'sheet.languages',
  resistances: 'sheet.resistances',
  advantages: 'sheet.advantages',
  disadvantages: 'sheet.disadvantages',
  level: 'sheet.level',
  'spell.save_dc': 'spells.saveDc',
  'spell.attack': 'spells.attack',
}

/** Ключ характеристики в путях величин: «dex» — от «dexterity». */
const ABILITY_BY_KEY: Readonly<Record<string, AbilityName>> = Object.fromEntries(
  ABILITY_NAMES.map((name) => [name.slice(0, 3), name]),
) as Readonly<Record<string, AbilityName>>

/**
 * Характеристика, если путь про неё: «check.dex» → «dexterity».
 * Нужна там, где подпись уже объясняет, что это за бросок:
 * «Проверка: Проверка Ловкости» — не подпись, а заикание.
 */
export function abilityOfPath(path: string): AbilityName | null {
  const [head, ...rest] = path.split('.')
  if (head !== 'check' && head !== 'saves' && head !== 'abilities') return null
  return ABILITY_BY_KEY[rest[0] ?? ''] ?? null
}

/**
 * Подпись величины по её пути.
 *
 * Машинный путь на экран попадать не должен — ни в разделе «прочие»
 * листа, ни в разборе, ни в журнале бросков. Часть путей известна
 * поимённо, часть собирается по образцу: спасброски, проверки,
 * атаки и хиты за уровень заводит движок, и образец у них один.
 *
 * Возвращает `null`, если подписи нет: величина без подписи —
 * не ошибка показа, а знак, что движок научился считать что-то новое.
 * Показать её всё равно надо, но об этом решает вызывающий.
 */
export function valueLabel(t: TFunction, path: string, id?: string): string | null {
  const known = VALUE_KEYS[path]
  if (known !== undefined) return t(known)

  const [head, ...rest] = path.split('.')
  const tail = rest.join('.')

  if (head === 'abilities') {
    const ability = ABILITY_BY_KEY[rest[0] ?? '']
    if (ability !== undefined) return t(`abilities.${ability}`)
  }
  if (head === 'saves') {
    const ability = ABILITY_BY_KEY[tail]
    if (ability !== undefined)
      return t('trait.save', { name: t(`abilities.${ability}`) })
  }
  if (head === 'check') {
    const ability = ABILITY_BY_KEY[tail]
    if (ability !== undefined) {
      return t('sheet.abilityCheck', { name: t(`abilities.${ability}`) })
    }
    return t('trait.skill', { name: contentName(t, tail) })
  }
  if (head === 'attack' || head === 'damage') {
    // Оружие называется своим именем: путь собран из записи предмета,
    // и `id` приходит оттуда же, если он известен вызывающему.
    const name = contentName(t, id ?? `srd:${tail}`)
    return t(head === 'attack' ? 'combat.attackOf' : 'combat.damageOf', { name })
  }
  if (head === 'hp' && tail.startsWith('level_')) {
    return t('sheet.hpLevel', { level: Number(tail.slice('level_'.length)) })
  }
  return null
}
